import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { ForgeUiAnalyticsTracker } from '../../analytics/useForgeUiAnalyticsEvent';
import { FeatureFlagEnv, FeatureFlagEventTypes } from '../../custom-ui/bridge/types';
import type { GQLExtension } from '../../web-client/graphql/types';
import { getFeatureFlags } from '../../web-client/queries/getFeatureFlags';
import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';
import { getAppType } from './utils';

// ─── Shared helpers ──────────────────────────────────────────────────────────

type FeatureFlagEventParams = Omit<
	Parameters<ForgeUiAnalyticsTracker['trackForgeFeatureFlagEvent']>[0],
	'appType' | 'appId' | 'baseUrl' | 'forgeEnvironment'
>;

const fireFeatureFlagAnalyticsEvent = (
	tracker: Pick<ForgeUiAnalyticsTracker, 'trackForgeFeatureFlagEvent'>,
	extension: GQLExtension,
	appId: string,
	params: FeatureFlagEventParams,
): void => {
	tracker.trackForgeFeatureFlagEvent({
		appType: getAppType(extension),
		appId,
		baseUrl: window.location.origin,
		forgeEnvironment: extension.environmentType,
		...params,
	});
};

// ─── initFeatureFlags ─────────────────────────────────────────────────────────

type InitFeatureFlagsPayload = {
	config: unknown;
	user?: {
		statsigEnvironment?: { tier: FeatureFlagEnv };
	} & Record<string, unknown>;
};

type InitFeatureFlagsContext = MakeBridgeContext<
	'extension' | 'extensionDetails' | 'contextIds' | 'environment',
	'getContextToken' | 'tracing' | 'analytics'
>;

/**
 * Initialises the Forge feature flags client SDK inside the iframe.
 *
 * Fetches a context token then POSTs to forge-outbound-proxy `/client/feature-flags`
 * with the user and config provided by the app.
 *
 * On success: returns the raw JSON response (feature flag assignments for the app).
 * On failure: logs to console and returns `{}` so the app degrades gracefully
 * rather than crashing.
 * Analytics are fired (success or failure) when `platform_forge_ff_ui_analytics` is on.
 */
export const initFeatureFlags: TBridgeMethod<
	InitFeatureFlagsContext,
	InitFeatureFlagsPayload,
	object
> = async (context, payload) => {
	const {
		extensionDetails: { appId },
		extension,
		contextIds,
		environment,
		services: { getContextToken, tracing, analytics },
	} = context;

	if (!fg('platform_forge_ff_client_sdk')) {
		return {};
	}

	const contextToken = await getContextToken({ contextIdsOverride: contextIds });

	tracing.recordBridgeCall('getFeatureFlags - start');

	const featureFlagEnv = payload.user?.statsigEnvironment?.tier || FeatureFlagEnv.development;

	const trackInit = (success: boolean): void => {
		if (fg('platform_forge_ff_ui_analytics')) {
			fireFeatureFlagAnalyticsEvent(analytics.tracker, extension, appId, {
				eventType: FeatureFlagEventTypes.INITIALIZATION,
				success,
				featureFlagEnv,
			});
		}
	};

	try {
		const resp = await getFeatureFlags(
			appId,
			payload.user,
			payload.config,
			environment,
			contextToken,
		);

		tracing.recordBridgeCall('getFeatureFlags - end');
		trackInit(true);

		return resp;
	} catch (err) {
		tracing.recordBridgeCall('getFeatureFlags - end');
		// eslint-disable-next-line no-console
		console.error('There were errors while initialising feature flags client ', err);
		trackInit(false);

		return {};
	}
};

// ─── trackFeatureFlagEvent ────────────────────────────────────────────────────

type TrackFeatureFlagEventPayload = {
	properties: {
		environment: string;
		name?: string;
		success: unknown;
	};
	type: string;
};

const ALLOWED_EVENT_TYPES = [FeatureFlagEventTypes.CHECKFLAG.toUpperCase()];

const isValidFeatureFlagEventPayload = (data: TrackFeatureFlagEventPayload): boolean => {
	if (!data.type || !ALLOWED_EVENT_TYPES.includes(data.type.toUpperCase())) {
		return false;
	}

	if (
		data.type.toUpperCase() === FeatureFlagEventTypes.CHECKFLAG.toUpperCase() &&
		data.properties.name &&
		(data.properties.name.length < 3 || data.properties.name.length > 50)
	) {
		return false;
	}

	if (typeof data.properties.success !== 'boolean') {
		return false;
	}

	if (
		!(data.properties.environment && data.properties.environment.toLowerCase() in FeatureFlagEnv)
	) {
		return false;
	}

	return true;
};

type TrackFeatureFlagEventContext = MakeBridgeContext<
	'extension' | 'extensionDetails',
	'analytics'
>;

export const trackFeatureFlagEvent: TBridgeMethod<
	TrackFeatureFlagEventContext,
	TrackFeatureFlagEventPayload,
	void
> = async (context, data) => {
	if (!fg('platform_forge_ff_ui_analytics')) {
		return;
	}

	const {
		extensionDetails: { appId },
		extension,
		services: { analytics },
	} = context;

	try {
		if (!isValidFeatureFlagEventPayload(data)) {
			return;
		}

		fireFeatureFlagAnalyticsEvent(analytics.tracker, extension, appId, {
			flagName: data.properties.name || '',
			eventType: data.type as FeatureFlagEventTypes,
			success: data.properties.success as boolean,
			featureFlagEnv: data.properties.environment as FeatureFlagEnv,
		});
	} catch {
		// all errors are ignored
	}
};
